import _ from 'lodash';
import React from 'react';

import { MaybeUndef } from '../../../../types/system';
import { memoizedPartial } from '../../../../utils/function/memoized-partial';
import i18n from '../../../../utils/i18n/i18n';
import { AccordionItemContainer } from '../../accordion/accordion-item/accordion-item';
import { OperationRow } from '../../operations-table/operation-row/operation-row';
import { WithRetryPanel } from '../../operations-table/with-retry-panel/with-retry-panel';
import {
  FileUploadOperation,
  FileUploadStatusType,
  OperationStatus,
} from '../operation-groups.types';
import { UploadOperationThumb } from '../upload-operation-thumb/upload-operation-thumb';

function getStatistics(operations: FileUploadOperation[]): {
  [id: string]: number;
} {
  return _.countBy(operations, (op: FileUploadOperation) => op.status.type);
}

const statusesMap = {
  [FileUploadStatusType.Canceled]: OperationStatus.Canceled,
  [FileUploadStatusType.Failed]: OperationStatus.Failed,
  [FileUploadStatusType.Uploaded]: OperationStatus.Succeeded,
  [FileUploadStatusType.Uploading]: OperationStatus.InProgress,
};

function uploadStatusToGenericOperationStatus(
  uploadStatus: FileUploadStatusType,
): OperationStatus {
  return statusesMap[uploadStatus];
}

function getTitle(statistics: { [id: string]: number }): string {
  const uploadedCount = statistics[FileUploadStatusType.Uploaded] || 0;
  const uploadingCount = statistics[FileUploadStatusType.Uploading] || 0;
  const isCompleted: boolean = uploadingCount === 0;

  return isCompleted
    ? i18n.t('operation-panel.file-upload.title.completed', {
        count: uploadedCount,
      })
    : i18n.t('operation-panel.file-upload.title.in-progress', {
        count: uploadingCount,
      });
}

function getLocateHandler(
  locateFile: (fileId: string) => void,
  operation: FileUploadOperation,
): MaybeUndef<() => void> {
  return operation.status.type === FileUploadStatusType.Uploaded
    ? memoizedPartial(locateFile, operation.status.fileId)
    : undefined;
}

interface Props {
  uploadOperationsList: FileUploadOperation[];
  closeGroup(groupId: string, operationsIds: string[]): any; // void;
  cancelFileUpload(clientId: string): any; // void;
  cancelAllUploads(): any; // void;
  groupId: string;
  locateFile(fileId: string): any; // void;
  retryUploadOperations(operationIds: string[]): any; // void
}

export class UploadGroup extends React.Component<Props> {
  private retryAll() {
    const { retryUploadOperations, uploadOperationsList } = this.props;

    retryUploadOperations(_.map(uploadOperationsList, 'id'));
  }

  private retryOne(id: string) {
    const { retryUploadOperations } = this.props;

    retryUploadOperations([id]);
  }

  public render() {
    const {
      groupId,
      closeGroup,
      cancelAllUploads,
      cancelFileUpload,
      uploadOperationsList,
      locateFile,
    } = this.props;

    const statistics = getStatistics(uploadOperationsList);
    const isCompleted: boolean =
      (statistics[FileUploadStatusType.Uploading] || 0) === 0;
    const closeClickHandler = isCompleted
      ? memoizedPartial(
          closeGroup,
          groupId,
          uploadOperationsList.map((op) => op.status.clientId),
        )
      : memoizedPartial(cancelAllUploads, groupId);

    return (
      <AccordionItemContainer
        key={groupId}
        id={groupId}
        title={getTitle(statistics)}
        onCloseClick={closeClickHandler}
      >
        <WithRetryPanel
          onRetryAllClick={this.retryAll.bind(this)}
          canceled={statistics[FileUploadStatusType.Canceled] || 0}
          failed={statistics[FileUploadStatusType.Failed] || 0}
        >
          {_.map(uploadOperationsList, (uploadOp) => (
            <OperationRow
              key={uploadOp.id}
              thumb={<UploadOperationThumb uploadStatus={uploadOp.status} />}
              name={uploadOp.name}
              size={uploadOp.size}
              progress={uploadOp.progress}
              status={uploadStatusToGenericOperationStatus(
                uploadOp.status.type,
              )}
              onCancelClick={memoizedPartial(
                cancelFileUpload,
                uploadOp.status.clientId,
              )}
              onLocateClick={getLocateHandler(locateFile, uploadOp)}
              cancelButtonHint={i18n.t('operation-panel.file-upload.cancel')}
              onRetryClick={memoizedPartial(
                this.retryOne.bind(this),
                uploadOp.id,
              )}
            />
          ))}
        </WithRetryPanel>
      </AccordionItemContainer>
    );
  }
}
